/**
 * Core-100 R2 — CORE-BR-GAP-001: the shop's outdoor-weather policy reaches the place work is
 * scheduled (OptiPlace), as a NOTICE beside the decision — never a filter. The constitution
 * forbids weather blocking work autonomously, so outdoor bays stay exactly as selectable as
 * before; the person sees the shop's own threshold crossed (or that weather could not be checked)
 * and decides. Mounts the weather read only when the shop has bays designated outdoor.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOutdoorWeatherAdvisory } from '@/hooks/weather/useOutdoorWeatherAdvisory';
import { OutdoorWeatherAdvisory } from '@/components/weather/OutdoorWeatherAdvisory';
import type { AdvisoryBay } from '@/lib/weather/outdoorAdvisory';

function Advisory({ bays }: { bays: AdvisoryBay[] }) {
  const { assessment, advisories } = useOutdoorWeatherAdvisory(bays);
  return <OutdoorWeatherAdvisory assessment={assessment} advisories={advisories} />;
}

export function SchedulingWeatherAdvisory({ shopId }: { shopId: string | null | undefined }) {
  const { data: outdoorBays } = useQuery({
    queryKey: ['scheduling-weather-outdoor-bays', shopId],
    enabled: !!shopId,
    queryFn: async (): Promise<AdvisoryBay[]> => {
      const { data, error } = await supabase
        .from('shop_bays')
        .select('id, name, is_outdoor')
        .eq('shop_id', shopId!)
        .eq('is_outdoor', true);
      if (error) throw error;
      return ((data ?? []) as AdvisoryBay[]).filter((b) => (b as { is_outdoor?: boolean | null }).is_outdoor === true);
    },
  });
  if (!outdoorBays || outdoorBays.length === 0) return null;
  return <div data-testid="scheduling-weather-advisory"><Advisory bays={outdoorBays} /></div>;
}
